import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { submissionsApi } from '../services/api';
import { DeckSubmission, DeckSubmissionUpdate, DeckSubmissionBreakdown } from '../types';
import { useTimezone } from '../context/TimezoneContext';
import { formatDateTime } from '../utils/datetime';

const DeckSubmissionManagement: React.FC = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<DeckSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<DeckSubmission | null>(null);
  const [editingSubmission, setEditingSubmission] = useState<DeckSubmission | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { timezone } = useTimezone();

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await submissionsApi.getAll();
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to load submissions:', error);
      setError('Failed to load submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmission = async (submissionData: DeckSubmissionUpdate) => {
    if (!editingSubmission) return;
    
    try {
      setError(null);
      const response = await submissionsApi.update(editingSubmission.id, submissionData);
      setSubmissions(submissions.map(s => s.id === editingSubmission.id ? response.data : s));
      setEditingSubmission(null);
      setSelectedSubmission(response.data);
    } catch (error: any) {
      console.error('Failed to update submission:', error);
      setError('Failed to update submission. Please try again.');
    }
  };

  const handleDeleteSubmission = async (submissionId: string) => {
    if (!window.confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(submissionId);
      setError(null);
      await submissionsApi.delete(submissionId);
      setSubmissions(submissions.filter(s => s.id !== submissionId));
      setSelectedSubmission(null);
    } catch (error: any) {
      console.error('Failed to delete submission:', error);
      setError('Failed to delete submission. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const handleSubmitSubmission = async (submissionId: string) => {
    try {
      setError(null);
      const response = await submissionsApi.submit(submissionId);
      setSubmissions(submissions.map(s => s.id === submissionId ? response.data : s));
      if (selectedSubmission?.id === submissionId) {
        setSelectedSubmission(response.data);
      }
    } catch (error: any) {
      console.error('Failed to submit deck:', error);
      setError('Failed to submit deck. Please try again.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading submissions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Deck Submissions</h1>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {editingSubmission && (
          <EditSubmissionForm
            submission={editingSubmission}
            onSubmit={handleEditSubmission}
            onCancel={() => setEditingSubmission(null)}
          />
        )}

        {selectedSubmission && (
          <SubmissionDetails
            submission={selectedSubmission}
            onClose={() => setSelectedSubmission(null)}
            onEdit={() => setEditingSubmission(selectedSubmission)}
            onDelete={() => handleDeleteSubmission(selectedSubmission.id)}
            onSubmit={() => handleSubmitSubmission(selectedSubmission.id)}
            isDeleting={deleting === selectedSubmission.id}
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {submissions.map((submission) => (
            <div
              key={submission.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-2">{submission.deck_name}</h3>
              <p className="text-gray-600 mb-2">
                Tournament: {submission.tournament?.name || 'Unknown'}
              </p>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Status:</span>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}>
                    {submission.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Cost:</span>
                  <span className="text-sm font-medium">€{submission.total_cost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Created:</span>
                  <span className="text-sm font-medium">{formatDateTime(submission.created_at, timezone)}</span>
                </div>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/deck/${submission.id}`);
                  }}
                  className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  View Details
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSubmission(submission);
                  }}
                  className="flex-1 bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  Quick View
                </button>
              </div>
            </div>
          ))}
        </div>

        {submissions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No submissions found. Create your first deck submission!</p>
          </div>
        )}
      </div>
    </div>
  );
};

interface EditSubmissionFormProps {
  submission: DeckSubmission;
  onSubmit: (submission: DeckSubmissionUpdate) => void;
  onCancel: () => void;
}

const EditSubmissionForm: React.FC<EditSubmissionFormProps> = ({ submission, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<DeckSubmissionUpdate>({
    deck_name: submission.deck_name,
    decklist_text: submission.decklist_text,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.deck_name || !formData.decklist_text) {
      alert('Deck name and decklist are required');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Edit Submission</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Deck Name *
          </label>
          <input
            type="text"
            value={formData.deck_name}
            onChange={(e) => setFormData({ ...formData, deck_name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter deck name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Decklist *
          </label>
          <textarea
            value={formData.decklist_text}
            onChange={(e) => setFormData({ ...formData, decklist_text: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={10}
            placeholder="Enter decklist (one card per line, format: quantity card_name)"
            required
          />
        </div>

        <div className="flex space-x-4">
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Update Submission
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

interface SubmissionDetailsProps {
  submission: DeckSubmission;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSubmit: () => void;
  isDeleting: boolean;
}

const SubmissionDetails: React.FC<SubmissionDetailsProps> = ({ 
  submission, onClose, onEdit, onDelete, onSubmit, isDeleting 
}) => {
  const { timezone } = useTimezone();
  const [breakdown, setBreakdown] = useState<DeckSubmissionBreakdown | null>(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);

  const loadBreakdown = useCallback(async () => {
    try {
      setLoadingBreakdown(true);
      setBreakdownError(null);
      const response = await submissionsApi.getBreakdown(submission.id);
      setBreakdown(response.data);
    } catch (error: any) {
      console.error('Failed to load breakdown:', error);
      setBreakdownError('Failed to load deck breakdown');
    } finally {
      setLoadingBreakdown(false);
    }
  }, [submission.id]);

  useEffect(() => {
    if (submission) {
      loadBreakdown();
    }
  }, [submission, loadBreakdown]);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">{submission.deck_name}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Tournament</label>
              <p className="text-gray-900">{submission.tournament?.name || 'Unknown'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                submission.status === 'DRAFT' ? 'bg-gray-100 text-gray-800' :
                submission.status === 'SUBMITTED' ? 'bg-yellow-100 text-yellow-800' :
                submission.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                'bg-red-100 text-red-800'
              }`}>
                {submission.status}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Cost</label>
              <p className="text-gray-900">€{submission.total_cost.toFixed(2)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Created</label>
              <p className="text-gray-900">{formatDateTime(submission.created_at, timezone)}</p>
            </div>
          </div>

          {submission.validation_errors && submission.validation_errors.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Validation Errors</label>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <ul className="text-red-700 space-y-1">
                  {submission.validation_errors.map((error, index) => {
                    const isFormatError = error.includes('not legal in');
                    const isBanlistError = error.includes('banned by this ruleset');
                    
                    return (
                      <li key={index} className="flex items-start">
                        <span className="mr-2">
                          {isFormatError ? '⚠️' : isBanlistError ? '🚫' : '•'}
                        </span>
                        <span className={isFormatError ? 'font-medium' : isBanlistError ? 'font-medium' : ''}>
                          {error}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}

          {submission.rejection_reason && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Rejection Reason</label>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700">{submission.rejection_reason}</p>
              </div>
            </div>
          )}

          {/* Copy Decklist Button */}
          <div>
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(submission.decklist_text);
                  alert('Decklist copied to clipboard!');
                } catch (err) {
                  console.error('Failed to copy decklist:', err);
                  alert('Failed to copy decklist to clipboard');
                }
              }}
              className="mb-3 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Copy Decklist
            </button>
          </div>

          {/* Deck Breakdown with Prices */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Deck Breakdown with Prices</label>
            {loadingBreakdown && (
              <div className="text-center py-4">
                <div className="text-gray-600">Loading deck breakdown...</div>
              </div>
            )}
            {breakdownError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700">{breakdownError}</p>
              </div>
            )}
            {breakdown && !loadingBreakdown && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Card Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Set
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Qty
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price Each
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {breakdown.cards.map((card, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                            {card.card_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {card.set_code || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {card.quantity}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            €{card.price.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                            €{card.total_price.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={4} className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                          Total Deck Cost:
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-900">
                          €{breakdown.total_cost.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-between">
          <div className="flex space-x-2">
            {submission.status === 'DRAFT' && (
              <>
                <button
                  onClick={onEdit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={onSubmit}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Submit for Review
                </button>
              </>
            )}
            {submission.status === 'DRAFT' && (
              <button
                onClick={onDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeckSubmissionManagement;
